<script setup lang="ts">
import { ref, computed } from 'vue'
import { imageFileToBase64 } from '../../../utils/imageToBase64'

const props = withDefaults(defineProps<{
  modelValue: string | null
  label?: string
  readonly?: boolean
  maxWidth?: number
  maxHeight?: number
  accept?: string
  error?: string | null
}>(), {
  label: 'Image',
  readonly: false,
  maxWidth: 200,
  maxHeight: 200,
  accept: 'image/png,image/jpeg,image/webp,image/svg+xml',
  error: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const fileInput = ref<HTMLInputElement | null>(null)
const busy = ref(false)
const localError = ref<string | null>(null)

const previewSrc = computed(() => props.modelValue || '')
const hasImage = computed(() => !!props.modelValue)

const sizeKb = computed(() => {
  const v = props.modelValue
  if (!v) return 0
  const comma = v.indexOf(',')
  const b64 = comma >= 0 ? v.slice(comma + 1) : v
  return Math.round((b64.length * 3) / 4 / 1024)
})

const onPick = () => {
  if (props.readonly) return
  fileInput.value?.click()
}

const onFile = async (e: Event) => {
  const target = e.target as HTMLInputElement
  const f = target.files?.[0]
  if (!f) return
  localError.value = null
  busy.value = true
  try {
    const dataUri = await imageFileToBase64(f, {
      maxWidth: props.maxWidth,
      maxHeight: props.maxHeight,
    })
    emit('update:modelValue', dataUri)
  } catch (err: any) {
    localError.value = err?.message || 'Failed to read image'
  } finally {
    busy.value = false
    target.value = ''
  }
}

const onClear = () => {
  if (props.readonly) return
  emit('update:modelValue', '')
}

const displayError = computed(() => localError.value || props.error)
</script>

<template>
  <div>
    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
      {{ label }}
    </label>
    <div class="mt-1 flex items-start gap-4">
      <div
        class="w-28 h-28 flex items-center justify-center rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 overflow-hidden"
      >
        <img v-if="hasImage" :src="previewSrc" class="max-w-full max-h-full object-contain" alt="logo preview" />
        <span v-else class="text-xs text-gray-400">No image</span>
      </div>

      <div class="flex flex-col gap-2">
        <input
          ref="fileInput"
          type="file"
          :accept="accept"
          class="hidden"
          @change="onFile"
        />
        <div class="flex gap-2">
          <button
            type="button"
            :disabled="readonly || busy"
            @click="onPick"
            class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {{ busy ? 'Processing…' : hasImage ? 'Replace' : 'Choose image' }}
          </button>
          <button
            v-if="hasImage && !readonly"
            type="button"
            @click="onClear"
            class="inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-700 rounded-md text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
          >
            Remove
          </button>
        </div>
        <p class="text-xs text-gray-500">
          Resized to max {{ maxWidth }}×{{ maxHeight }}. Stored inline as base64.
          <span v-if="hasImage">· ~{{ sizeKb }} KB</span>
        </p>
        <p v-if="displayError" class="text-xs text-red-600 dark:text-red-400">{{ displayError }}</p>
      </div>
    </div>
  </div>
</template>
